import { defineStore } from 'pinia';
import { UserState, User } from './types';
import { getEmployeeSecure, mapEmployeeSecure } from '@/shared/services/access-control';
import {
  ProductEnum,
  RegistrationStates,
  SignRight,
  SignType,
  UserContractsFragment,
  VerificationStatus,
} from '@/app/graphql/access-control/sdk.access-control';
import { ORGANIZATION } from '@/shared/web/type';
import { usePaymentStore } from '../payment';
import { useNotificationStore } from '../notification';
import { useHistoryTransactionStore } from '../history-transaction';
import { nativeApp } from '@/shared/services/native-app';
import { getNptByLegalPartyId } from '@/shared/services/account';
import { useKeyAvailability } from '@/shared/lib/use-key-availability';
import { useMobileVersion } from '@/shared/lib/use-mobile-version';
import { checkPaymentActionLimit } from '@/shared/services/action-limit/service';

export const useAuthUserStore = defineStore('authUser', {
  state: (): UserState => ({
    user: {
      id: null,
      email: '',
      mobile: '',
      fullName: '',
      shortName: '',
      createdDate: '',
      individualInn: '',
      userReferenceId: null,
      currentContract: null,
      authorized: false,
      userContracts: null,
      postVerificationStatus: null,
    },
    isSignUpdateAvailable: false,
    isSignSaveAvailable: false,
    showUserApproveDialog: false,
    isUserNotFound: false,
    nptData: [],
    transactionKeyAvailability: {
      available: true,
      nonAvailableFor: [],
      orgReferenceId: 0,
    },
    authorization: {
      isSmsConfirmation: false,
    },
  }),
  getters: {
    shortName: (state) => state.user.shortName,
    userId: (state) => state.user.userReferenceId,
    authorized: (state) => state.user.authorized,
    selectedUserContract: (state) => state.user.currentContract,
    userContracts: (state) => state.user.userContracts,
    isContractPayControl: (state) => state.user.currentContract?.signType === SignType.PayControl,
    isShowActions: (state) => {
      return state.user.currentContract?.userContractAttributes?.signRight !== SignRight.Auditor;
    },
    isCurrentDigital: (state) => state.user.currentContract?.isDigital,
    isHasAllowedCreatePayment: (state) => {
      const blockSigns = [
        SignRight.First,
        SignRight.Second,
        SignRight.Creator,
        SignRight.Cashier,
        SignRight.CashManager,
      ];
      const signRight = state.user.currentContract?.userContractAttributes?.signRight;
      return signRight && !blockSigns.includes(signRight);
    },
    isCreator: (state) => state.user.currentContract?.userContractAttributes?.signRight === SignRight.Creator,
    isFirstSign: (state) => state.user.currentContract?.userContractAttributes?.signRight === SignRight.First,
    isSecondSign: (state) => state.user.currentContract?.userContractAttributes?.signRight === SignRight.Second,
    isAuditor: (state) => state.user.currentContract?.userContractAttributes?.signRight === SignRight.Auditor,
    isSignNone: (state) => state.user.currentContract?.userContractAttributes?.signRight === SignRight.None,
    isCashier: (state) => {
      return state.user.currentContract?.userContractAttributes?.signRight === SignRight.Cashier;
    },
    isCashierOnMerchantVerification: (state) => {
      return (
        state.user.currentContract?.userContractAttributes?.registrationState ===
        RegistrationStates.OnMerchantVerification
      );
    },
    isCashManager: (state) => {
      return state.user.currentContract?.userContractAttributes?.signRight === SignRight.CashManager;
    },
    isUserLocked: (state) => state.user.postVerificationStatus === VerificationStatus.Pending,
    isUserRejected: (state) => state.user.postVerificationStatus === VerificationStatus.Rejected,
    isHasDebt: (state) => state.nptData.length,
    isHasSalaryProject: (state) => {
      return !!state.user.currentContract?.userContractAttributes?.productRoles?.filter(
        (item) => item.productName === ProductEnum.SalaryProject
      ).length;
    },
  },
  actions: {
    async getUser() {
      try {
        const getSecureData = async () => {
          const cached = localStorage.getItem('employeeSecure');
          if (useMobileVersion(191) && cached) return mapEmployeeSecure(JSON.parse(cached));

          return await getEmployeeSecure();
        };

        const res = await getSecureData();

        if (!res.userContracts.length) {
          this.isUserNotFound = true;
          nativeApp.loading(false);
          return;
        }

        this.setUser(res);
      } catch (e: any) {
        console.error('Пользователь не авторизован');
        if (e?.graphQLErrors[0]?.extensions?.classification === 'UserNotFoundException') {
          this.isUserNotFound = true;
          nativeApp.loading(false);
          return nativeApp.localStorageLoaded();
        }
        nativeApp.sessionExpired();
      }
      this.checkFirstSignAvailable();
    },
    async getKeyAvailability() {
      this.transactionKeyAvailability = await useKeyAvailability().fetchKeyAvailability();
    },
    setUser(payload: User) {
      this.user = payload;
    },
    logout() {
      nativeApp.logout();
      this.$reset();
      usePaymentStore().reset();
      useNotificationStore().reset();
      useHistoryTransactionStore().reset();
    },
    logoutToPinCode() {
      nativeApp.sessionExpired();
      this.$reset();
      usePaymentStore().reset();
      useNotificationStore().reset();
      useHistoryTransactionStore().reset();
    },
    setUserAuthorized(payload: boolean) {
      this.user.authorized = payload;
    },
    setUserSelectedContract(payload: UserContractsFragment, setNative: boolean = true) {
      const orgId = localStorage.getItem(ORGANIZATION.ORG_ID_KEY);
      if (!orgId || (orgId && setNative)) {
        localStorage.setItem(ORGANIZATION.ORG_ID_KEY, payload.orgReferenceId.toString());
        nativeApp.setOrgReferenceId(payload.orgReferenceId.toString());
      }
      this.user.currentContract = payload;
      const { reset } = useHistoryTransactionStore();
      reset();
    },
    setUserContractFromLocalStorage(id?: string) {
      const orgId = id ? id : localStorage.getItem(ORGANIZATION.ORG_ID_KEY);
      console.log('local storage', localStorage.getItem(ORGANIZATION.ORG_ID_KEY));
      const contract = this.userContracts?.find((contract) => contract.orgReferenceId === +(orgId || ''));

      if (contract) this.setUserSelectedContract(contract, false);
      else if (this.userContracts?.length) this.setUserSelectedContract(this.userContracts[0], false);

      nativeApp.localStorageLoaded();
    },
    setIsSmsConfirmation(value: boolean) {
      this.authorization.isSmsConfirmation = value;
    },
    async getNpt() {
      this.nptData = [];

      if (!this.selectedUserContract?.orgReferenceId || this.isCashier || this.isCashManager) return;
      try {
        this.nptData = await getNptByLegalPartyId(this.selectedUserContract.orgReferenceId);
      } catch (e) {
        console.error(e);
      }
    },

    async checkFirstSignAvailable() {
      const setDefault = () => {
        this.isSignSaveAvailable = true;
        this.isSignUpdateAvailable = true;
      };

      if (!this.selectedUserContract) return setDefault();

      try {
        const orgId = this.selectedUserContract.orgReferenceId;

        const [save, update] = await Promise.all([
          checkPaymentActionLimit(orgId, 'savePayment'),
          checkPaymentActionLimit(orgId, 'updatePayment'),
        ]);

        this.isSignSaveAvailable = save?.data ?? true;
        this.isSignUpdateAvailable = update?.data ?? true;
      } catch {
        setDefault();
      }
    },
  },
});
